/**
 * COMPONENT_SPEC §2 — the primitives. OWNER: web-frontend-engineer.
 *
 * Three laws every one of them inherits (COMPONENT_SPEC §0.3):
 *   L-A  no border, fill, shadow or hover ground on anything containing cloth;
 *   L-B  on a cloth screen the only legal grounds are the fabric surrounds;
 *   L-C  nothing is dimmed and nothing is disabled. NO COMPONENT HERE HAS A
 *        `disabled` PROP. Where an action is unavailable, the control is
 *        replaced by a `Statement`; where a VALUE is unavailable it carries
 *        `aria-disabled` and stays keyboard-reachable with its reason in the
 *        accessible name. The HTML `disabled` attribute appears nowhere.
 *
 * Every visual property resolves to a custom property emitted from
 * design/tokens.json. There is not one literal in this file or its stylesheet.
 */

import type { ButtonHTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { useId } from 'react';

/* ------------------------------------------------------------------ Button */

export type ButtonVariant = 'primary' | 'secondary';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  readonly variant?: ButtonVariant;
  /** `ST-CTL-BUSY` — same place, same size, label to the present participle. */
  readonly busy?: boolean;
  /** The busy label. Always supplied from the catalogue, never derived here. */
  readonly busyLabel?: string;
  /** `ST-CTL-UNAVAILABLE` — aria-disabled, still reachable, reason in the name. */
  readonly unavailable?: boolean;
  readonly unavailableReason?: string;
  readonly children: ReactNode;
}

export function Button({
  variant = 'secondary',
  busy = false,
  busyLabel,
  unavailable = false,
  unavailableReason,
  children,
  className,
  onClick,
  ...rest
}: ButtonProps) {
  const classes = [
    'pt-button',
    `pt-button--${variant}`,
    busy ? 'pt-button--pending' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      aria-busy={busy ? true : undefined}
      aria-disabled={unavailable || busy ? true : undefined}
      {...(unavailable && unavailableReason !== undefined
        ? { 'aria-label': unavailableReason }
        : {})}
      onClick={(event) => {
        // Cannot double-submit, and an unavailable control announces rather
        // than acting. Never the HTML `disabled` attribute (North Star §8.3).
        if (busy || unavailable) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {busy && busyLabel !== undefined ? busyLabel : children}
    </button>
  );
}

/* -------------------------------------------------------------------- Link */

export interface LinkProps {
  readonly href: string;
  readonly onNavigate?: (href: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
  readonly ariaLabel?: string;
}

/**
 * A real anchor. `type.role.link` is a MODIFIER: it sets no family and no size,
 * so a link inherits the prose it sits in — which is what makes the swatch
 * offer one system at two sizes (RULING-009 consequence).
 */
export function Link({ href, onNavigate, children, className, ariaLabel }: LinkProps) {
  return (
    <a
      href={href}
      className={['type-role-link', className ?? ''].filter(Boolean).join(' ')}
      {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
      onClick={(event) => {
        if (onNavigate === undefined) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onNavigate(href);
      }}
    >
      {children}
    </a>
  );
}

/* --------------------------------------------------------------- Statement */

export type StatementTone = 'region' | 'field';

export interface StatementProps {
  readonly children: ReactNode;
  /** `region` = the standing statement (supporting); `field` = replacing a control. */
  readonly tone?: StatementTone;
  readonly id?: string;
  /** Announced once, politely, only where it genuinely replaces a control's news. */
  readonly announce?: boolean;
  readonly className?: string;
}

/**
 * `EL-STATEMENT`. The product's single honesty pattern.
 *
 * Structurally forbidden here, and unbuildable because no class or token
 * offers it: a container, a border, a fill, a tint, an icon, an alert colour,
 * a dismiss control, an overlay, a toast, a floating bar, a left rule.
 * It is removed by data, never by hand.
 */
export function Statement({
  children,
  tone = 'region',
  id,
  announce = false,
  className,
}: StatementProps) {
  return (
    <p
      {...(id === undefined ? {} : { id })}
      className={['pt-statement', `pt-statement--${tone}`, className ?? '']
        .filter(Boolean)
        .join(' ')}
      {...(announce ? { role: 'status' } : {})}
    >
      {children}
    </p>
  );
}

/* ---------------------------------------------------------------- FactLine */

export interface FactLineProps {
  readonly label?: string;
  readonly children: ReactNode;
  readonly emphasis?: boolean;
  readonly className?: string;
}

/**
 * THE UNIFORM FIELD. One style for every stated fact in the product, which is
 * how `[T-37]`, `[T-26]`, RULING-004 and the DOM-diff test are satisfied by
 * construction rather than by discipline: there is no second field style
 * available to reach for. `color.alert.*` may never bind here.
 */
export function FactLine({ label, children, emphasis = false, className }: FactLineProps) {
  const classes = ['pt-fact', emphasis ? 'pt-fact--emphasis' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  if (label === undefined) return <p className={classes}>{children}</p>;
  return (
    <div className={classes}>
      <span className="pt-fact__label">{label}</span> <span className="pt-fact__value">{children}</span>
    </div>
  );
}

/** A description-list pair, where a screen reader must speak label-then-value. */
export function FactPair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pt-fact pt-fact--pair">
      <dt className="pt-fact__label">{label}</dt>
      <dd className="pt-fact__value">{children}</dd>
    </div>
  );
}

/* ----------------------------------------------------------- SelectionMark */

/**
 * Selection, legible without colour: a filled square in a fixed leading
 * position. Decorative — state is carried by `aria-checked` on the option.
 */
export function SelectionMark({ checked, radio = false }: { checked: boolean; radio?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'pt-mark',
        radio ? 'pt-mark--radio' : '',
        checked ? 'pt-mark--on' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

/* -------------------------------------------------------- RovingRadioGroup */

export interface RovingRadioGroupProps {
  /** The accessible name of the group (a resolved string, never a key). */
  readonly ariaLabel: string;
  readonly values: readonly string[];
  /** The chosen value, or `''` when none is selected yet. */
  readonly chosen: string;
  readonly labelOf: (value: string) => string;
  readonly onChoose: (value: string) => void;
  /**
   * When true, activating the already-chosen option clears the group — the
   * filter semantics. Arrow keys always SELECT (never clear), per the WAI-ARIA
   * radiogroup pattern.
   */
  readonly toggle?: boolean;
  /** Layout class for the value row. Defaults to the filter row. */
  readonly rowClassName?: string;
}

/**
 * A radiogroup that is actually operable by keyboard — the pattern
 * `OptionGroup.rove()` already gets right, extracted so the five simpler groups
 * (book facets, enum measurement authoring, the queue stage filter, the halt
 * code, the config hero toggle) cannot drift from it (accessibility BLOCKER,
 * SC 2.1.1 / 4.1.2).
 *
 * Two rules make it keyboard-complete:
 *   · ROVING TABINDEX with a first-item fallback — when nothing is selected the
 *     first option is the single tab stop, so the group is never a zero-tab-stop
 *     dead zone; once a value is chosen the chosen option is the tab stop.
 *   · ARROW KEYS move focus AND selection within the group and wrap, so every
 *     option is reachable without a mouse.
 */
export function RovingRadioGroup({
  ariaLabel,
  values,
  chosen,
  labelOf,
  onChoose,
  toggle = false,
  rowClassName = 'pt-filter__values',
}: RovingRadioGroupProps) {
  const selectedIndex = values.findIndex((v) => v === chosen);
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const next = (index + (forward ? 1 : -1) + values.length) % values.length;
    const target = event.currentTarget.parentElement?.children[next];
    if (target instanceof HTMLElement) target.focus();
    const value = values[next];
    if (value !== undefined) onChoose(value);
  };
  return (
    <div className={rowClassName} role="radiogroup" aria-label={ariaLabel}>
      {values.map((value, index) => {
        const isChosen = value === chosen;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            className="pt-option"
            aria-checked={isChosen}
            tabIndex={selectedIndex === -1 ? (index === 0 ? 0 : -1) : isChosen ? 0 : -1}
            onKeyDown={(event) => move(event, index)}
            onClick={() => onChoose(toggle && isChosen ? '' : value)}
          >
            {/* THE MARK, AND THE `__label` SPAN INSIDE `__body`. Both were
                missing, and together they meant NOTHING ON SCREEN SAID WHICH
                OPTION WAS CHOSEN in any group that used this component — the
                halt code, the queue's stage filter, the enum measurement
                fields, and every group ROAD-2/4/5 added. `aria-checked` was
                right, so a screen reader knew and a sighted person did not,
                which is the wrong half to get right.

                Found by looking at a screenshot of the payment panel. The
                selected-state rule has always existed
                (`.pt-option[aria-checked='true'] .pt-option__label`) — it just
                had no `__label` to bite on here, because only the
                configurator's own `OptionGroup` rendered one. */}
            <SelectionMark checked={isChosen} radio />
            <span className="pt-option__body">
              <span className="pt-option__label">{labelOf(value)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- Skeleton */

export interface SkeletonProps {
  /** How many rows to reserve, at the exact final row height. */
  readonly rows?: number;
  readonly variant?: 'ledger' | 'table' | 'tile' | 'block';
  readonly label?: string;
  readonly slow?: boolean;
  readonly slowText?: string;
}

/**
 * The final layout, held still. Its whole job is to reserve space, so it has no
 * shimmer, no pulse and no gradient sweep — no token exists for one, and no
 * repeating driver exists anywhere in this product.
 */
export function Skeleton({ rows = 6, variant = 'block', label, slow = false, slowText }: SkeletonProps) {
  return (
    <div className="pt-skeleton" aria-busy="true" {...(label === undefined ? {} : { 'aria-label': label })}>
      <div aria-hidden="true">
        {Array.from({ length: rows }, (_unused, index) => (
          <div key={index} className={`pt-skeleton__row pt-skeleton__row--${variant}`} />
        ))}
      </div>
      {slow && slowText !== undefined ? <Statement tone="field">{slowText}</Statement> : null}
    </div>
  );
}

/* --------------------------------------------------------- ErrorStatement */

export interface ErrorStatementProps {
  /** The four parts of `[T-74]`: what happened · what it means · what to do · who to write to. */
  readonly what: string;
  readonly means?: string;
  readonly todo?: string;
  readonly who?: string;
  readonly id?: string;
  /** A refused submission uses the ONE assertive region; everything else is polite. */
  readonly assertive?: boolean;
  readonly children?: ReactNode;
}

/**
 * The ONLY component permitted to bind `color.alert.ink`. Errors are typeset
 * statements in the flow — never toasts, never overlays, never modals, and
 * never on a screen that renders cloth.
 */
export function ErrorStatement({
  what,
  means,
  todo,
  who,
  id,
  assertive = false,
  children,
}: ErrorStatementProps) {
  const generated = useId();
  return (
    <div
      id={id ?? generated}
      className="pt-error"
      {...(assertive ? { role: 'alert' } : { role: 'status' })}
      tabIndex={-1}
    >
      <p className="pt-error__line">{what}</p>
      {means === undefined ? null : <p className="pt-error__line">{means}</p>}
      {todo === undefined ? null : <p className="pt-error__line">{todo}</p>}
      {who === undefined ? null : <p className="pt-error__line">{who}</p>}
      {children}
    </div>
  );
}

/* -------------------------------------------------------------- TextInput */

export interface TextInputProps {
  readonly id?: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'password' | 'email' | 'date';
  readonly multiline?: boolean;
  readonly hint?: string;
  readonly error?: string;
  readonly autoComplete?: string;
  readonly inputMode?: 'text' | 'decimal' | 'numeric';
  readonly onEnter?: () => void;
  readonly name?: string;
  readonly describedBy?: string;
}

/**
 * Collect text where collecting text is legitimate. The exhaustive list of
 * screens is COMPONENT_SPEC §2.5; it does not include any screen that shows a
 * client his record, and gate G4 asserts that in the rendered output.
 *
 * Label always visible, above. Never a placeholder as a label. No disabled state.
 */
export function TextInput({
  id,
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
  hint,
  error,
  autoComplete,
  inputMode,
  onEnter,
  name,
  describedBy,
}: TextInputProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const described =
    [hint === undefined ? '' : hintId, error === undefined ? '' : errorId, describedBy ?? '']
      .filter(Boolean)
      .join(' ') || undefined;

  const shared = {
    id: fieldId,
    name: name ?? fieldId,
    value,
    className: 'pt-input__field',
    'aria-invalid': error === undefined ? undefined : true,
    'aria-describedby': described,
    onChange: (event: { target: { value: string } }) => onChange(event.target.value),
  };

  return (
    <div className="pt-input">
      <label className="pt-input__label" htmlFor={fieldId}>
        {label}
      </label>
      {multiline ? (
        <textarea {...shared} rows={4} />
      ) : (
        <input
          {...shared}
          type={type}
          {...(autoComplete === undefined ? {} : { autoComplete })}
          {...(inputMode === undefined ? {} : { inputMode })}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter !== undefined) {
              event.preventDefault();
              onEnter();
            }
          }}
        />
      )}
      {hint === undefined ? null : (
        <p id={hintId} className="pt-input__hint">
          {hint}
        </p>
      )}
      {error === undefined ? null : <ErrorStatement id={errorId} what={error} />}
    </div>
  );
}
